import React from 'react';

/* Matrix answer mini table — colour tier from the rating's position in the
 * question's scale (best → worst); works for presets and custom scales. */
const MATRIX_PRESETS = {
  excellent_poor: ['Excellent', 'Good', 'Average', 'Poor'],
  always_never: ['Always', 'Usually', 'Sometimes', 'Never'],
  agree_disagree: ['Strongly Agree', 'Agree', 'Disagree', 'Strongly Disagree'],
  stars: ['5 Stars', '4 Stars', '3 Stars', '2 Stars', '1 Star'],
  csat_4pt: ['Very Satisfied', 'Satisfied', 'Unsatisfied', 'Very Unsatisfied'],
  expectations_3pt: ['Exceeded expectations', 'Met expectations', 'Below expectations'],
  likelihood_4pt: ['Very Likely', 'Likely', 'Unlikely', 'Very Unlikely'],
  yes_no: ['Yes', 'No'],
};
const BADGE_TIERS = [
  'bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]',
  'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]',
  'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]',
  'bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]',
];
const NEUTRAL_TIER = 'bg-[#F1F5F9] text-[#334155] border-[#E2E8F0]';

function scaleLabels(q) {
  const scale = q?.scale || 'excellent_poor';
  if (scale === 'custom') {
    const c = Array.isArray(q?.custom_scale) ? q.custom_scale.filter(Boolean) : [];
    return c.length >= 2 ? c : MATRIX_PRESETS.excellent_poor;
  }
  return MATRIX_PRESETS[scale] || MATRIX_PRESETS.excellent_poor;
}

function badgeClass(value, labels, isCustom) {
  const idx = labels.indexOf(value);
  if (isCustom || idx < 0) return NEUTRAL_TIER;
  if (labels.length === 1) return BADGE_TIERS[0];
  return BADGE_TIERS[Math.round((idx / (labels.length - 1)) * (BADGE_TIERS.length - 1))];
}

export default function MatrixAnswerTable({ matrix, questionDef }) {
  const items = Array.isArray(matrix?.items) ? matrix.items : [];
  const defined = (Array.isArray(questionDef?.criteria) ? questionDef.criteria : []).filter(Boolean);
  const criteria = defined.length ? defined : Array.from(new Set(items.flatMap((r) => Object.keys(r || {})).filter((k) => k !== 'item')));
  const labels = scaleLabels(questionDef);
  const isCustom = questionDef?.scale === 'custom';
  if (!items.length || !criteria.length) return <p className="text-xs italic text-[#94A3B8]">(no matrix ratings)</p>;
  const singleAxis = items.every((r) => !r || !r.item);
  return (
    <div className="overflow-x-auto border border-[#E2E8F0] rounded-md" data-testid="matrix-answer-table">
      <table className="w-full text-xs">
        <thead className="bg-[#F8FAFC]">
          <tr>
            {!singleAxis && <th className="text-left px-2 py-1.5 font-semibold text-[#0F172A]">Item</th>}
            {criteria.map((c) => <th key={c} className="text-left px-2 py-1.5 font-semibold text-[#0F172A]">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={i} className="border-t border-[#F1F5F9]">
              {!singleAxis && <td className="px-2 py-1.5 text-[#0F172A]">{row.item || `(item ${i + 1})`}</td>}
              {criteria.map((c) => (
                <td key={c} className="px-2 py-1.5">
                  {row[c]
                    ? <span className={`inline-block px-2 py-0.5 rounded border text-[10px] ${badgeClass(row[c], labels, isCustom)}`}>{row[c]}</span>
                    : <span className="text-[10px] text-[#94A3B8] italic">—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
